import React, { Fragment, useCallback, useContext } from 'react';
import { Field, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import Checkbox from 'components/Form/Checkbox';
import inputField from 'components/Form/inputField';
import TextArea from 'components/Form/TextArea';
import { validate as validateForm, bytesToMegaBytes } from 'components/Form/utils';
import {
  isRequired,
  isPositiveNumber,
  isGreaterOrEqualThan,
  isValidFile,
} from 'components/Form/validationRules';
import ImagePicker, { getFileChecksum } from 'components/Form/ImagePicker';
import { presignUrl, uploadToS3 } from 'api/events';
import { NotificationAlertsContext, ALERT_TYPES } from 'components/NotificationAlerts';
import apiBase from 'api/base';

const NewEventStep3 = () => {
  const { createAlert } = useContext(NotificationAlertsContext);
  const access_token = useSelector(state => state?.session?.access_token);
  const form = useForm();

  const onFileChange = useCallback(
    file => {
      form.change('avatar', '');

      if (bytesToMegaBytes(file.size) > 1) {
        createAlert('El archivo debe pesar 1MB como máximo.', ALERT_TYPES.ERROR);

        return null;
      }

      getFileChecksum(file).then(checksum => {
        const image = {
          byte_size: file.size,
          filename: file.name,
          content_type: file.type,
          checksum,
        };

        try {
          presignUrl(image).then(({ status, body }) => {
            if (status !== 200) {
              throw new Error('Presign Failed');
            }

            // Amazon requires the request to not have authorization token
            apiBase.jwt();
            uploadToS3(body, file).then(({ status: uploadStatus }) => {
              // restore token
              apiBase.jwt(access_token);

              if (uploadStatus !== 201) {
                throw new Error('Upload Failed');
              }

              form.change('avatar', body?.blob_signed_id);
            });
          });
        } catch (_) {
          // restore token
          apiBase.jwt(access_token);

          createAlert(
            'El archivo seleccionado no puede ser procesado. Porfavor intenta nuevamente. Si el error persiste recomendamos que seleccione otro archivo.',
            ALERT_TYPES.ERROR,
          );
        }
      });
    },
    [createAlert, form, access_token],
  );

  return (
    <Fragment>
      <h2 className="text-brandblue text-2xl font-bold mb-4">Paso 3</h2>

      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">Costo de inscripción</label>
        <Field
          name="registration_cost"
          component="input"
          type="number"
          placeholder="Costo de inscripción"
          className="border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg"
          min={0}
        />
      </div>
      <div>
        <Field
          defaultValue={false}
          type="checkbox"
          component={inputField(Checkbox)}
          name="allow_subscription_payment"
          inputProps={{
            label: (
              <label className="font-roboto text-xl block">Aceptar pagos de inscripción</label>
            ),
          }}
        />
      </div>

      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">Minimo de integrantes</label>
        <Field
          name="minimum_members"
          component="input"
          type="number"
          placeholder="Minimo de integrantes"
          className="border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg"
          min={0}
        />
      </div>
      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">Cover / Imagen del evento</label>
        <Field name="cover_file" component={inputField(ImagePicker)} onChange={onFileChange} />
      </div>
      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block" htmlFor="name">
          Nombre del evento
        </label>
        <Field
          name="name"
          component="input"
          placeholder="Nombre del evento"
          className="border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg"
        />
      </div>
      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block" htmlFor="description">
          Descripción del evento / Reglas del evento
        </label>
        <Field
          name="description"
          component={inputField(TextArea)}
          inputProps={{
            rows: 5,
            placeholder: 'Descripción del evento / Reglas del evento',
            className: 'border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg',
          }}
        />
      </div>
    </Fragment>
  );
};

export const validations = {
  registration_cost: [isRequired, isPositiveNumber],
  minimum_members: [isRequired, isPositiveNumber, isGreaterOrEqualThan(1)],
  name: [isRequired],
  avatar: [isRequired, isValidFile('cover_file')],
  description: [isRequired],
};

export const validate = validateForm({ validations, raw: true, messages: true });

export default NewEventStep3;
